/**
 * <chart-east> — East Indian kundali: a 3×3 grid whose four corner cells are each
 * split by a diagonal, giving eight triangles and four whole edge cells.
 *
 * ⚠ **A third geometry, not a restyling of the South Indian chart.** It is
 * rāśi-fixed like the South Indian one, but the rāśis run **anticlockwise** from
 * Meṣa at top centre where the South Indian chart runs clockwise from Meṣa in the
 * top row. Meṣa, Karka, Tulā and Makara sit at the four edge centres; the other
 * eight are triangles in the corners. See src/utils/svg.js for the cell map and
 * how it was read off the classical Figure 1.
 *
 * ◆ Like the South Indian style this needs no bhāvas, so it draws for a birth
 * with no recorded time.
 *
 * © Manish Jagdish Thatte
 */

import {
  EAST_ANCHORS, EAST_CELLS, EAST_LINES, el, polygonPath, renderCellLabels
} from '../utils/svg.js';
import { graha, rashi } from '../jyotish/constants.js';
import { localName } from '../utils/i18n.js';

export class ChartEast extends HTMLElement {
  static observedAttributes = ['locale', 'labels', 'title-text'];

  connectedCallback() { this.render(); }
  attributeChangedCallback() { if (this.isConnected) this.render(); }

  set kundali(value) { this._kundali = value; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    const k = this._kundali;
    this.textContent = '';
    if (!k) return;

    const locale = this.getAttribute('locale') || 'en';
    const useSymbols = this.getAttribute('labels') === 'symbols';

    const svg = el('svg', {
      viewBox: '-2 -2 104 104',
      class: 'chart chart--east',
      role: 'img',
      'aria-label': k.lagna
        ? `East Indian chart, lagna in ${localName(k.lagna.rashi.name, locale)}`
        : 'East Indian chart, birth time unknown so no lagna is shown'
    });

    const lines = el('g', { class: 'chart__lines' });
    for (const [[x1, y1], [x2, y2]] of EAST_LINES) {
      lines.append(el('line', { x1, y1, x2, y2 }));
    }
    svg.append(lines);

    // ⚠ In the document before the cells are filled, so text can be measured.
    this.append(svg);

    const byRashi = Array.from({ length: 12 }, () => []);
    for (const g of Object.values(k.grahas)) byRashi[g.rashiIndex].push(g.graha);
    const lagnaRashi = k.lagna ? k.lagna.rashiIndex : null;

    for (let i = 0; i < 12; i++) {
      const cell = EAST_CELLS[i];
      const g = el('g', { class: 'chart__cell', 'data-rashi': i });
      g.append(el('path', { d: polygonPath(cell), class: 'chart__cell-area' }));
      svg.append(g);

      // ◆ The rāśi label sits at the cell's inner corner, which is where the śāstra's
      // figure puts it — the triangles have no room anywhere else.
      const [lx, ly] = labelAnchor(cell, i);
      g.append(el('text', {
        x: lx, y: ly, class: 'chart__rashi-label', 'text-anchor': 'middle'
      }, rashi(i).abbr));

      const occupants = byRashi[i];
      if (occupants.length) {
        renderCellLabels(g, occupants.map((id) => localName(graha(id).name, locale)), {
          cell,
          anchor: EAST_ANCHORS[i],
          makeSpan: grahaSpan(k, occupants, locale),
          abbreviate: (_, idx) => graha(occupants[idx] ?? occupants[0]).name.symbol,
          baseFont: useSymbols ? 4 : 4.4
        });
      }

      if (i === lagnaRashi) g.classList.add('is-lagna');
    }

    const centre = el('g', { class: 'chart__centre' });
    const title = this.getAttribute('title-text');
    if (title) {
      centre.append(el('text', {
        x: 50, y: 48, class: 'chart__title', 'text-anchor': 'middle'
      }, title));
    }
    if (!k.bhavas) {
      centre.append(el('text', {
        x: 50, y: title ? 56 : 51, class: 'chart__centre-note', 'text-anchor': 'middle'
      }, 'no bhāvas'));
    }
    svg.append(centre);
  }
}

/** One graha's <tspan>, with its state classes and a tooltip when abbreviated. */
function grahaSpan(kundali, occupants, locale) {
  return (label, index, abbreviated) => {
    const id = occupants[index] ?? occupants[0];
    const info = kundali.grahas[id];
    const span = el('tspan', {
      class: [
        'chart__graha',
        info?.retrograde ? 'is-retrograde' : null,
        info?.combust ? 'is-combust' : null
      ].filter(Boolean).join(' '),
      'data-graha': id
    }, label + (info?.retrograde ? '\u2009\u211E' : ''));
    if (abbreviated) span.append(el('title', {}, localName(graha(id).name, locale)));
    return span;
  };
}


/** The inner corner of each cell, where Figure 1 writes the rāśi abbreviation. */
function labelAnchor(cell, rashiIndex) {
  const t = 100 / 3;
  const inner = {
    1: [t - 4.5, t - 4.5], 2: [t - 7.5, t - 1.0],     // Ta, Ge — top-left corner
    4: [t - 7.5, 2 * t + 4.2], 5: [t - 4.0, 2 * t + 7.6], // Le, Vi — bottom-left
    7: [2 * t + 4.0, 2 * t + 7.6], 8: [2 * t + 7.5, 2 * t + 4.2], // Sc, Sg — bottom-right
    10: [2 * t + 7.5, t - 1.0], 11: [2 * t + 4.5, t - 4.5]        // Aq, Pi — top-right
  }[rashiIndex];
  if (inner) return inner;
  // Edge-centre cells have room: put the label at the outer edge.
  const xs = cell.map((p) => p[0]), ys = cell.map((p) => p[1]);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return [cx + (cx - 50) * 0.0, cy + (cy - 50) * 0.62 + 1.6];
}

customElements.define('chart-east', ChartEast);
